import {
  faBriefcase,
  faEllipsisV,
  faFolder,
  faGlasses,
  faGraduationCap,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import { EngagementRole } from "backend/model/engagementRole";
import { EngagementState } from "backend/model/engagementState";
import {
  APTVersion,
  APTVersionType,
  WorkspaceTreeNode,
} from "src/app/workspace/workspace-list/workspace-list.component";
import { EngagementPurposeType } from "backend/model/engagementPurposeType";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from "@angular/core";
import { StandardIconComponent } from "src/standard/standard-icon/standard-icon.component";
import { StandardButtonComponent } from "src/standard/standard-button/standard-button.component";
import { StandardLineClampComponent } from "src/standard/standard-line-clamp/standard-line-clamp.component";
import { StandardContextMenuComponent } from "src/standard/standard-context-menu/standard-context-menu.component";
import { IStandardButtonDropdownItem } from "src/standard/standard-button-dropdown/standard-button-dropdown.component";
import { StandardButtonDropdownActionsComponent } from "src/standard/standard-button-dropdown/standard-button-dropdown-actions/standard-button-dropdown-actions.component";
import { StandardTooltip } from "src/standard/standard-tooltip/standard-tooltip.component";
import { APTContext } from "src/app/services/aptContext/apt-context";

export interface MenuHoverEvent {
  node: WorkspaceTreeNode;
  menu:
    | StandardButtonDropdownActionsComponent<WorkspaceTreeNode>
    | StandardContextMenuComponent<WorkspaceTreeNode>;
}
export interface MenuToggleEvent {
  node: WorkspaceTreeNode;
  menu:
    | StandardButtonDropdownActionsComponent<WorkspaceTreeNode>
    | StandardContextMenuComponent<WorkspaceTreeNode>;
  wasOpened: boolean;
}

@Component({
  standalone: true,
  selector: "app-workspace-list-tree-item",
  templateUrl: "./workspace-list-tree-item.component.html",
  host: {
    class: "w-100 d-flex align-items-center",
  },
  styles: [
    `
      .sublabel {
        color: var(--apt-manatee-color);
        font-weight: 600;
      }

      .icon-holder {
        min-height: 14px;
        min-width: 26.33px;
      }

      .actions-holder {
        min-height: 30px;
        min-width: 26.97px;
      }

      .archived {
        opacity: 0.6 !important;
      }
    `,
  ],
  imports: [
    StandardTooltip,
    StandardIconComponent,
    StandardButtonComponent,
    StandardLineClampComponent,
    StandardButtonDropdownActionsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspaceListTreeItemComponent {
  faFolder = faFolder;
  faGlasses = faGlasses;
  faBriefcase = faBriefcase;
  faEllipsisV = faEllipsisV;
  faGraduationCap = faGraduationCap;
  faLock = faLock;

  APTVersion = APTVersion;
  EngagementRole = EngagementRole;
  EngagementState = EngagementState;
  EngagementPurposeType = EngagementPurposeType;

  protected readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly aptContext = inject(APTContext);

  archiveTooltipText = $localize`Archived`;
  disabledRestoreTooltipText = $localize`Restore failed`;
  restoreTooltipText = $localize`Engagement is in remote storage. Press to restore to archived filter in the workspace list.`;
  crossBorderTooltipText = $localize`You cannot perform a restore operation on this engagement as it is a cross-border engagement.`;
  externalReviewerTooltipText = $localize`External reviewer workspace`;
  trainingTooltipText = $localize`Training workspace`;
  isOfflineWebMode = this.aptContext.isOfflineWebMode;

  node = input.required<WorkspaceTreeNode>();
  aptVersion = input.required<APTVersionType>();
  contextMenuLoading = input.required<boolean>();
  contextMenuItems =
    input.required<IStandardButtonDropdownItem<WorkspaceTreeNode>[]>();
  overrideEngagementState = signal<EngagementState | undefined>(undefined);

  readonly onMenuHover = output<MenuHoverEvent>();
  readonly onMenuToggled = output<MenuToggleEvent>();
  readonly onRestoreRemoteClicked = output<WorkspaceTreeNode>();
  readonly onWorkspaceClicked = output<WorkspaceTreeNode>();
  readonly onWorkspaceDoubleClicked = output<WorkspaceTreeNode>();

  dropdownMenu =
    viewChild.required<
      StandardButtonDropdownActionsComponent<WorkspaceTreeNode>
    >("dropdownMenu");

  contextMenu =
    viewChild<StandardContextMenuComponent<WorkspaceTreeNode>>("contextMenu");

  handleMenuHover(
    node: WorkspaceTreeNode,
    menu:
      | StandardButtonDropdownActionsComponent<WorkspaceTreeNode>
      | StandardContextMenuComponent<WorkspaceTreeNode>,
  ) {
    this.onMenuHover.emit({
      node,
      menu,
    });
  }

  restoreTooltip = computed(() => {
    switch (this.state()) {
      case EngagementState.Restoring:
        return $localize`Restoring`;
      case EngagementState.RestoreFailed:
        return $localize`Restore failed`;
      case EngagementState.EngagementDeletionFailed:
        return $localize`Delete failed`;
      case EngagementState.InTransit:
        return $localize`Moving to remote storage`;
      case EngagementState.InTransitFailed:
        return $localize`Move to remote storage failed`;
      case EngagementState.Deleted:
        return $localize`Deleted`;
      default:
        return this.node().data.memberFirmId ==
          this.aptContext.userMemberFirmId()
          ? this.restoreTooltipText
          : this.crossBorderTooltipText;
    }
  });

  state = computed(
    () => this.overrideEngagementState() ?? this.node().data.state,
  );

  restoreDisabled = computed(() => {
    const isCrossBorder =
      this.aptContext.userMemberFirmId() !== this.node().data.memberFirmId;
    return (
      (this.state() !== EngagementState.RemoteStorage &&
        this.state() !== EngagementState.DeletionPending) ||
      isCrossBorder
    );
  });

  isArchived = computed(() => {
    const state = this.state();
    return (
      state === EngagementState.Archived ||
      state === EngagementState.Archiving ||
      state === EngagementState.RemoteStorage ||
      state === EngagementState.Restoring ||
      state === EngagementState.RestoreFailed ||
      state === EngagementState.EngagementDeletionFailed ||
      state === EngagementState.DeletionPending ||
      state === EngagementState.InTransit ||
      state === EngagementState.InTransitFailed
    );
  });

  isInRemoteStorage = computed(() => {
    const state = this.state();
    return (
      state === EngagementState.RemoteStorage ||
      state === EngagementState.Restoring ||
      state === EngagementState.RestoreFailed ||
      state === EngagementState.EngagementDeletionFailed ||
      state === EngagementState.DeletionPending ||
      state === EngagementState.InTransit ||
      state === EngagementState.InTransitFailed
    );
  });

  handleMenuToggled(
    node: WorkspaceTreeNode,
    menu:
      | StandardButtonDropdownActionsComponent<WorkspaceTreeNode>
      | StandardContextMenuComponent<WorkspaceTreeNode>,
    wasOpened = false,
  ) {
    this.onMenuToggled.emit({
      node,
      menu,
      wasOpened,
    });
  }

  handleRestoreRemoteClicked(node: WorkspaceTreeNode) {
    this.onRestoreRemoteClicked.emit(node);
  }

  handleWorkspaceClicked(node: WorkspaceTreeNode) {
    this.onWorkspaceClicked.emit(node);

    const parentTreeNode = this.host.nativeElement.closest(".p-treenode");
    if (parentTreeNode) {
      (parentTreeNode as HTMLElement).focus();
    }
  }

  handleWorkspaceDoubleClicked(node: WorkspaceTreeNode) {
    this.onWorkspaceDoubleClicked.emit(node);
  }

  async handleContextMenu(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    await this.dropdownMenu().dropdown().toggle(event);
  }
}
